import { RecoverableError } from "../../Error/Error";
import { alternative } from "../../Utils/Utils";

export type PrimaryCTA = {
  type: "PrimaryCTA";
  text: string;
  href: string;
  ariaLabel?: string;
  ariaAttrs?: Record<string, string>;
  daaLl?: string | null;
};

export type SecondaryCTA = {
  type: "SecondaryCTA";
  text: string;
  href: string;
  ariaLabel?: string;
  ariaAttrs?: Record<string, string>;
  daaLl?: string | null;
};

export type ProductEntryCTA = PrimaryCTA | SecondaryCTA;

const parseCTA = (
  type: Pick<CTA, 'type'>
) => (
  element: Element | null
): Parsed<CTA, RecoverableError> => {
  if (element === null)
    throw new Error('');

  const anchor = element.querySelector(getSelector(type));
  if (!anchor)
    throw new Error('');

  const textContent = anchor.textContent ?? '';
  const [text = '', pipeAriaLabel = ''] = textContent.split('|').map((s) => s.trim());
  if (text === '')
    throw new Error('');

  const href = anchor.getAttribute('href') ?? '';
  if (href === '')
    throw new Error('');

  const daaLl = anchor.getAttribute('daa-ll');
  const ariaLabelAttr = anchor.getAttribute('aria-label')?.trim() ?? '';
  const ariaLabel = ariaLabelAttr !== ''
    ? ariaLabelAttr
    : (pipeAriaLabel !== '' ? pipeAriaLabel : undefined);
  const es: RecoverableError[] = [];

  return [
    {
      type: type.type,
      text,
      href,
      ariaLabel,
      daaLl: typeof daaLl === 'string' && daaLl.trim() !== '' ? daaLl : text,
    },
    es
  ]
}

export const parsePrimaryCTA = parseCTA({ type: "PrimaryCTA" });

export const parseSecondaryCTA = parseCTA({ type: "SecondaryCTA" });

export const parseProductEntryCTA = (
  element: Element | null
): Parsed<ProductEntryCTA, RecoverableError> => alternative(parsePrimaryCTA)
  .or(parseSecondaryCTA)
  .eval(element);

type CTA
  = PrimaryCTA
  | SecondaryCTA;

const getSelector = ({ type }: Pick<CTA, 'type'>): string => {
  switch (type) {
    case "PrimaryCTA": return 'strong > a';
    case "SecondaryCTA": return 'em > a';
    default: throw new Error('');
  }
}

